/**
 * Standalone Form Page
 *
 * A no-layout page that renders forms outside the main application layout.
 *
 * URL Structure: /ff/[form-key]/[submissionId?]?view=xxx
 * - formKey: Required - The key of the form to load
 * - submissionId: Optional - Specific submission ID to load/edit
 * - view: Query parameter - 'form' (default) or 'chat'
 *
 * Examples:
 * - /ff/contact-form - New form submission in form view
 * - /ff/contact-form?view=chat - New form submission in chat view
 * - /ff/contact-form/sub-123 - Edit existing submission in form view
 * - /ff/contact-form/sub-123?view=chat - Edit existing submission in chat view
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { SimpleFormProvider } from '../fluidComponents/fluidForm/SimpleFormLoader';
function postParentFrameMessage(type, data) {
    if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type, data }, '*');
    }
}
function saveSubmissionIdInLocalStorage(formKey, submissionId) {
    localStorage.setItem(`fluidform:submission:${formKey}`, submissionId);
}
function getSubmissionIdFromLocalStorage(formKey) {
    return localStorage.getItem(`fluidform:submission:${formKey}`);
}
export const StandaloneFormPage = () => {
    const { formKey, submissionId: submissionIdParam } = useParams();
    const [submissionIdFromStorage, setSubmissionId] = useState(submissionIdParam || getSubmissionIdFromLocalStorage(formKey));
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const view = searchParams.get('view') || 'form';
    const mode = searchParams.get('mode');
    const chatPath = searchParams.get('chatPath') || 'chat';
    // Ref so that the stale closure inside SimpleFormLoader's listener always reads the current submission ID.
    const currentSubmissionIdRef = useRef(submissionIdParam);
    // Handle submission creation - update URL with new submissionId without reloading
    const handleSubmissionCreated = (newSubmissionId) => {
        let currentPath = window.location.pathname;
        let newPath;
        const existingId = currentSubmissionIdRef.current;
        if (existingId) {
            // Replace the existing submission ID in the path (e.g. after "Submit another response")
            newPath = currentPath.replace(existingId, String(newSubmissionId));
        }
        else {
            // First submission - append the submission ID to the path
            newPath = currentPath + '/' + newSubmissionId;
        }
        currentSubmissionIdRef.current = String(newSubmissionId);
        // Navigate to the new URL without reloading the page
        navigate(newPath + window.location.search, { replace: true });
        setSubmissionId(String(newSubmissionId));
        saveSubmissionIdInLocalStorage(formKey, String(newSubmissionId));
    };
    const isHeadless = mode === 'headless';
    // When embedded (headless), make background transparent and post height to parent
    useEffect(() => {
        if (!isHeadless)
            return;
        try {
            document.body.style.background = 'transparent';
        }
        catch (_) { }
        const computeHeight = () => {
            try {
                const b = document.body;
                const e = document.documentElement;
                return Math.max(b ? b.scrollHeight : 0, e ? e.scrollHeight : 0, b ? b.offsetHeight : 0, e ? e.offsetHeight : 0);
            }
            catch (_) {
                return undefined;
            }
        };
        const postHeight = () => {
            const h = computeHeight();
            if (typeof h === 'number') {
                postParentFrameMessage('fluidform-height', { height: h });
            }
        };
        // Observe size changes of body
        let ro;
        if ('ResizeObserver' in window) {
            ro = new ResizeObserver(() => postHeight());
            try {
                ro.observe(document.body);
            }
            catch (_) { }
        }
        else {
            const interval = setInterval(postHeight, 600);
            // Cleanup timer
            return () => { clearInterval(interval); };
        }
        // Initial posts
        postHeight();
        const onLoad = () => postHeight();
        const onResize = () => postHeight();
        window.addEventListener('load', onLoad);
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('load', onLoad);
            window.removeEventListener('resize', onResize);
            try {
                ro && ro.disconnect();
            }
            catch (_) { }
        };
    }, [isHeadless]);
    const handleMessageReceived = (message) => {
        postParentFrameMessage('fluidform-message', message);
    };
    return (React.createElement("div", { className: `view-${view} ${isHeadless
            ? 'w-full m-0 p-0 border-none shadow-none bg-transparent h-dvh'
            : 'flex justify-center items-stretch min-h-screen p-2 bg-gray-100'}` },
        React.createElement("div", { className: isHeadless
                ? 'h-dvh'
                : 'w-full max-w-[1200px] p-6' },
            React.createElement(SimpleFormProvider, { formKey: formKey, view: view, submissionId: submissionIdParam, onSubmissionCreated: handleSubmissionCreated, onMessageReceived: handleMessageReceived, autosave: true, headless: isHeadless, formProps: {
                    chatProps: {
                        chatPath: chatPath
                    }
                } }))));
};
export default StandaloneFormPage;
